
import React, { lazy } from 'react';
import _ from 'underscore';
import Promise from 'bluebird';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withFallbackPage } from '@bingads-webui-campaign-react/fallback-page';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { withPreference } from '@bingads-webui-react/preference-context';
import { withScope } from '@bingads-webui-react/scope-service';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { bundlesUseHighcharts } from './dynamic-routes';

const getLandingPaths = () => [
  '/campaign/vnext',
  '/campaign/vnext/ads',
  '/campaign/vnext/adgroups',
  '/campaign/vnext/campaigns',
  '/campaign/vnext/keywords',
  // TODO: enable DFM for campaign scope
  window.location.search.indexOf('campaignId') > -1 ? '' : '/campaign/vnext/overview',
  '/campaign/vnext/accounts',
  '/campaign/vnext/accounts/overview',
  '/campaign/vnext/accounts/overview/expert',
  '/campaign/vnext/accounts/overview/smart',
  '/campaign/vnext/accounts/performance',
];

let isLegacyDefineLoaded = false;

function pickImport(name, moduleExport) {
  return () => moduleExport().then(c => (c[name]));
}

function pickImportWithLazyInit(name, moduleExport) {
  return () => moduleExport().then(c => (c[name]()));
}

function triggerWillInit(WrappedComponent) {
  const WillInitWrapper = (props) => {
    const { perfMarker } = props;
    _.result(perfMarker, 'willInit');

    return (<WrappedComponent {...props} />);
  };

  WillInitWrapper.propTypes = {
    perfMarker: perfMarkerPropTypes.isRequired,
  };

  return React.memo(WillInitWrapper);
}

function importHighcharts() {
  return Promise.all([
    import(/* webpackMode: 'eager' */'highcharts'),
    import(/* webpackMode: 'eager' */'highcharts/highcharts-more'),
    import(/* webpackMode: 'eager' */'highcharts/modules/accessibility'),
    import(/* webpackMode: 'eager' */'highcharts/modules/pattern-fill'),
  ]).then(([Highcharts, highchartsMore, accessibilityModule, patternFillModule]) => {
    highchartsMore.default(Highcharts);
    accessibilityModule.default(Highcharts);
    patternFillModule.default(Highcharts);
    return Highcharts;
  });
}

export function pickManyImports(names, moduleExport) {
  return _.mapObject(_.object(names, names), ((_v, name) => pickImport(name, moduleExport)));
}

export function pickManyImportsWithLazyInit(names, moduleExport) {
  return _.mapObject(_.object(names, names), ((_v, name) => pickImportWithLazyInit(name, moduleExport)));
}

export const getDefaultExport = e => e.default;

export function lazyLoad(componentFactory, applyWrappers = _.identity, skipWillInitTrigger = false) {
  if (!componentFactory) {
    throw new Error('Dynamic route does not exist!');
  }

  const wrapperFn = skipWillInitTrigger ? applyWrappers : _.compose(applyWrappers, triggerWillInit);

  const loadComponent = () => componentFactory().then(c => wrapperFn(c)).then(feature => ({ default: feature }));

  return lazy(loadComponent);
}

export function importLegacyDeps() {
  if (isLegacyDefineLoaded) {
    return Promise.resolve();
  }

  return import(/* webpackChunkName: "vendor.async.legacy-requirejs" */'../legacy-dep-bootstrap/require.config').then(() => {
    import(/* webpackChunkName: "vendor.async.legacy-requirejs" */'../legacy-dep-bootstrap/shim/requirejs-shim').then(() => {
      isLegacyDefineLoaded = true;
    });
  });
}

export function importAfterPreLoad(modulePath, moduleExport, needHighcharts = false, needLegacyDeps = false) {
  const loadHighChart = needHighcharts || bundlesUseHighcharts[modulePath];

  if (needLegacyDeps && loadHighChart) {
    return Promise.resolve().then(importLegacyDeps).then(importHighcharts).then(moduleExport);
  }

  if (needLegacyDeps) {
    return Promise.resolve().then(importLegacyDeps).then(moduleExport);
  }

  if (loadHighChart) {
    return Promise.resolve().then(importHighcharts).then(moduleExport);
  }

  return Promise.resolve().then(moduleExport);
}

export function importAfterPreLoadHighCharts(moduleExport) {
  return importHighcharts().then(moduleExport);
}

export function isLandingPage() {
  const currentPath = window.location.pathname;

  return getLandingPaths().indexOf(currentPath.toLowerCase()) > -1;
}

export const withContexts = _.compose(withI18nConsumer, withFallbackPage, withPermissions, withPreference, withScope);


export const withProps = (WrappedComponent, extraProps) => {
  const HoC = props => (
    <WrappedComponent {..._.extend({}, props, extraProps)} />
  );

  HoC.displayName = `WithProps(${getDisplayName(WrappedComponent)})`;

  hoistNonReactStatics(HoC, WrappedComponent);

  return HoC;
};

export function withPrefetch(WrappedComponent, prefetchChunks = _.noop) {
  // Using webpack will cause the browser to load chunks when the page is initialized
  const deferPrefetchChunks = () => _.defer(prefetchChunks);
  const HoC = props => (
    <WrappedComponent {...props} prefetchChunks={deferPrefetchChunks} />
  );

  HoC.displayName = `WithPrefetch(${getDisplayName(WrappedComponent)})`;

  hoistNonReactStatics(HoC, WrappedComponent);

  return HoC;
}
